/**
 * FERP PDF Studio Hub Page (DRR-079)
 *
 * Entry point for the full PDF suite. Surfaces PDF capabilities that
 * were previously buried inside the document viewer.
 *
 * Sections:
 * - Stat Cards: at-a-glance counts (total, pending, regulatory, compliance)
 * - Quick Open: paste URL or upload a file to open the PDF suite inline
 * - Recent Documents: last 10 PDF documents with regulatory indicators
 * - Tool Cards: 14 tools with distinct actions (upload, navigate, compare, modal)
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - Dumb Frontend: recent docs from backend query, tier from backend
 * - No new npm deps — reuses existing PDF components
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Paper,
  TextInput,
  Button,
  FileButton,
  SimpleGrid,
  Card,
  ThemeIcon,
  Center,
  Loader,
  Alert,
  Divider,
  Tooltip,
  ActionIcon,
  Modal,
  PasswordInput,
  Textarea,
  NumberInput,
  Select,
  Switch,
} from '@mantine/core';
import {
  IconFileTypePdf,
  IconLink,
  IconUpload,
  IconHighlight,
  IconSignature,
  IconColumns,
  IconEyeOff,
  IconDownload,
  IconFileStack,
  IconLock,
  IconAlertCircle,
  IconArrowRight,
  IconX,
  IconClockHour4,
  IconShieldCheck,
  IconScale,
  IconFiles,
  IconListCheck,
  IconForms,
  IconScissors,
  IconPrinter,
  IconLockAccess,
  IconCertificate,
  IconHistory,
  IconInfoCircle,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import { PDFViewer, ComparisonView } from '@/components/pdf';
import type { PdfAnnotation, PdfFeatureTier } from '@/components/pdf';
import {
  useListDocumentsQuery,
  useEncryptDocumentMutation,
  useFillStateFormMutation,
  useHtmlPdfTemplatesQuery,
  DocumentStatus,
} from '@/generated/graphql';

// Feature tier — in production this comes from tenant subscription via backend.
// For dev/demo, default to 'pro'.
const FEATURE_TIER: PdfFeatureTier = 'pro';

// ============================================================================
// Tool Card Definitions
// ============================================================================

type ToolAction =
  | { type: 'upload' }            // Open file picker → viewer
  | { type: 'navigate'; to: string } // Navigate to a route
  | { type: 'compare' }           // Dual file upload → comparison view
  | { type: 'modal'; modal: string } // Open a specific modal

interface ToolCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  proOnly: boolean;
  action: ToolAction;
}

const TOOL_CARDS: ToolCard[] = [
  {
    title: 'Annotate',
    description: 'Highlight text, add comments, draw freehand, place sticky notes',
    icon: <IconHighlight size={24} />,
    proOnly: false,
    action: { type: 'upload' },
  },
  {
    title: 'Sign',
    description: 'Capture digital signatures with signature pad',
    icon: <IconSignature size={24} />,
    proOnly: true,
    action: { type: 'upload' },
  },
  {
    title: 'Compare',
    description: 'Side-by-side dual PDF viewer for document comparison',
    icon: <IconColumns size={24} />,
    proOnly: true,
    action: { type: 'compare' },
  },
  {
    title: 'Redact',
    description: 'Mark regions for server-side redaction of sensitive content',
    icon: <IconEyeOff size={24} />,
    proOnly: true,
    action: { type: 'upload' },
  },
  {
    title: 'Export',
    description: 'Flatten annotations into PDF and download',
    icon: <IconDownload size={24} />,
    proOnly: true,
    action: { type: 'upload' },
  },
  {
    title: 'Page Management',
    description: 'Reorder, rotate, and delete pages',
    icon: <IconFileStack size={24} />,
    proOnly: true,
    action: { type: 'upload' },
  },
  {
    title: 'Signing Ceremony',
    description: 'Step-through field completion with placed signature, initials & date fields',
    icon: <IconListCheck size={24} />,
    proOnly: true,
    action: { type: 'navigate', to: '/ferp-core/documents/templates' },
  },
  {
    title: 'XFA Form Fill',
    description: 'Fill Adobe LiveCycle XFA forms (state government PDFs)',
    icon: <IconForms size={24} />,
    proOnly: true,
    action: { type: 'modal', modal: 'formfill' },
  },
  {
    title: 'AcroForm Fill',
    description: 'Auto-fill standard PDF forms from FERP context (constellation + entity data)',
    icon: <IconForms size={24} />,
    proOnly: true,
    action: { type: 'modal', modal: 'formfill' },
  },
  {
    title: 'Extract Pages',
    description: 'Assign pages to categories or export as images (floor plans, diagrams)',
    icon: <IconScissors size={24} />,
    proOnly: true,
    action: { type: 'upload' },
  },
  {
    title: 'Batch Generate',
    description: 'Render multiple PDFs from YAML templates in one operation',
    icon: <IconPrinter size={24} />,
    proOnly: true,
    action: { type: 'modal', modal: 'batch' },
  },
  {
    title: 'Encrypt',
    description: 'AES-256-GCM password encryption for sensitive documents',
    icon: <IconLockAccess size={24} />,
    proOnly: true,
    action: { type: 'modal', modal: 'encrypt' },
  },
  {
    title: 'PDF/A Compliance',
    description: 'Validate and convert documents to PDF/A-3b archival format',
    icon: <IconCertificate size={24} />,
    proOnly: true,
    action: { type: 'modal', modal: 'pdfa' },
  },
  {
    title: 'Document Trail',
    description: 'View approval chain, regulatory compliance, and version history',
    icon: <IconHistory size={24} />,
    proOnly: true,
    action: { type: 'upload' },
  },
  {
    title: 'Metadata',
    description: 'Inspect PDF title, author, creator, page count, and properties',
    icon: <IconInfoCircle size={24} />,
    proOnly: false,
    action: { type: 'upload' },
  },
];

// ============================================================================
// Helper Components
// ============================================================================

function statusColor(status: string): string {
  switch (status) {
    case DocumentStatus.APPROVED:
    case DocumentStatus.PUBLISHED:
      return 'green';
    case DocumentStatus.PENDING_APPROVAL:
    case DocumentStatus.PENDING_VALIDATION:
      return 'blue';
    case DocumentStatus.REJECTED:
      return 'red';
    case DocumentStatus.ARCHIVED:
      return 'orange';
    case DocumentStatus.DRAFT:
      return 'gray';
    default:
      return 'gray';
  }
}

function relativeDate(iso: string | number): string {
  try {
    const date = new Date(String(iso));
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

const CARD_HOVER_STYLE = {
  cursor: 'pointer',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};

function StatCard({
  icon,
  color,
  label,
  value,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  value: number;
}) {
  return (
    <Card withBorder padding="md">
      <Group gap="sm">
        <ThemeIcon size="lg" variant="light" color={color} radius="md">
          {icon}
        </ThemeIcon>
        <div>
          <Text size="xl" fw={700} lh={1}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </div>
      </Group>
    </Card>
  );
}

// ============================================================================
// Modals
// ============================================================================

function EncryptModal({
  opened,
  onClose,
  tenantId,
}: {
  opened: boolean;
  onClose: () => void;
  tenantId: string | null;
}) {
  const [documentId, setDocumentId] = useState('');
  const [password, setPassword] = useState('');
  const [encryptDocument, { loading, data, error }] = useEncryptDocumentMutation();

  const handleEncrypt = useCallback(() => {
    if (!documentId || !tenantId) return;
    encryptDocument({
      variables: { input: { documentId, tenantId } },
    });
  }, [documentId, tenantId, encryptDocument]);

  return (
    <Modal opened={opened} onClose={onClose} title="Encrypt Document" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          AES-256-GCM encryption. Provide a document ID from your recent documents.
        </Text>
        <TextInput
          label="Document ID"
          placeholder="Enter document ID..."
          value={documentId}
          onChange={(e) => setDocumentId(e.currentTarget.value)}
          required
        />
        <PasswordInput
          label="Encryption Password"
          placeholder="Strong password..."
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        {error && (
          <Alert color="red" fz="sm">
            {error.message}
          </Alert>
        )}
        {data?.encryptDocument?.success && (
          <Alert color="green" fz="sm">
            Encrypted successfully. S3 key: {data.encryptDocument.encryptedS3Key}
          </Alert>
        )}
        <Button
          onClick={handleEncrypt}
          loading={loading}
          disabled={!documentId || !tenantId}
          fullWidth
        >
          Encrypt
        </Button>
      </Stack>
    </Modal>
  );
}

/**
 * FormFillModal — AcroForm + XFA fill via fillStateForm mutation.
 *
 * DRY: This modal works for BOTH AcroForm and XFA PDFs. The backend auto-detects
 * the form type (AcroForm vs XFA) and fills accordingly. The `flatten` toggle
 * determines whether the output is a flat PDF (no Adobe Reader needed) or keeps
 * the form interactive.
 *
 * Other subgraphs can reuse this pattern for any form-fill workflow:
 * 1. Define a FormFieldMap (ffm-*) that maps FERP fields to PDF form fields
 * 2. Provide a contextId (workflow instance or entity ID) for data resolution
 * 3. Call fillStateForm mutation
 */
function FormFillModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [formFieldMapId, setFormFieldMapId] = useState('');
  const [contextId, setContextId] = useState('');
  const [flatten, setFlatten] = useState(true);
  const [lockFilled, setLockFilled] = useState(false);
  const [fillForm, { loading, data, error, reset }] = useFillStateFormMutation();

  const handleFill = useCallback(() => {
    if (!formFieldMapId || !contextId) return;
    fillForm({
      variables: {
        input: {
          formFieldMapId,
          contextId,
          flatten,
          lockFilledFields: lockFilled,
        },
      },
    });
  }, [formFieldMapId, contextId, flatten, lockFilled, fillForm]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const result = data?.fillStateForm;

  return (
    <Modal opened={opened} onClose={handleClose} title="Fill PDF Form" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Auto-fill PDF forms (AcroForm or XFA) from FERP entity data.
          The backend resolves field values from the constellation + workflow instance context.
        </Text>
        <TextInput
          label="Form Field Map ID"
          placeholder="e.g., ffm-mn-dhs6790-001"
          description="Config ID from common/configs/seed/form-field-maps/"
          value={formFieldMapId}
          onChange={(e) => setFormFieldMapId(e.currentTarget.value)}
          ff="monospace"
          required
        />
        <TextInput
          label="Context ID"
          placeholder="Workflow instance ID or entity ID"
          description="Provides data context for field resolution (constellation + entity fields)"
          value={contextId}
          onChange={(e) => setContextId(e.currentTarget.value)}
          ff="monospace"
          required
        />
        <Switch
          label="Flatten output (no Adobe Reader needed)"
          description="Renders filled form as standard PDF graphics. Recommended for distribution."
          checked={flatten}
          onChange={(e) => setFlatten(e.currentTarget.checked)}
        />
        <Switch
          label="Lock pre-filled fields"
          description="Make auto-filled fields read-only. Manual fields remain editable."
          checked={lockFilled}
          onChange={(e) => setLockFilled(e.currentTarget.checked)}
        />
        {error && (
          <Alert color="red" fz="sm">
            {error.message}
          </Alert>
        )}
        {result && (
          <Alert color="green" fz="sm" title="Form Filled Successfully">
            <Stack gap="xs">
              <Text size="sm">Document ID: <Text span ff="monospace">{result.documentId}</Text></Text>
              <Text size="sm">{result.filledFields.length} fields auto-filled, {result.blankFields.length} left blank</Text>
              {result.warnings.length > 0 && (
                <Text size="xs" c="orange">
                  Warnings: {result.warnings.join('; ')}
                </Text>
              )}
            </Stack>
          </Alert>
        )}
        <Button
          onClick={handleFill}
          loading={loading}
          disabled={!formFieldMapId || !contextId}
          fullWidth
        >
          Fill Form
        </Button>
      </Stack>
    </Modal>
  );
}

function BatchGenerateModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [dataRowsJson, setDataRowsJson] = useState('[\n  {"name": "Example", "amount": "100"}\n]');
  const [maxConcurrent, setMaxConcurrent] = useState<number>(10);

  // Wire htmlPdfTemplates query (DRR-232 Track E)
  const { data: templatesData, loading: templatesLoading } = useHtmlPdfTemplatesQuery();
  const templateOptions = useMemo(() => {
    const templates = templatesData?.htmlPdfTemplates ?? [];
    return templates.map((t) => ({ value: t, label: t }));
  }, [templatesData]);

  return (
    <Modal opened={opened} onClose={onClose} title="Batch Generate PDFs" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Render multiple PDFs from a YAML template with different data per row.
        </Text>
        <Select
          label="Template"
          placeholder={templatesLoading ? 'Loading templates...' : 'Select a template'}
          data={templateOptions}
          value={templateId}
          onChange={setTemplateId}
          searchable
          clearable
          disabled={templatesLoading}
          required
        />
        <Textarea
          label="Data Rows (JSON array)"
          placeholder='[{"name": "Alice"}, {"name": "Bob"}]'
          value={dataRowsJson}
          onChange={(e) => setDataRowsJson(e.currentTarget.value)}
          minRows={5}
          autosize
          required
        />
        <NumberInput
          label="Max Concurrent"
          value={maxConcurrent}
          onChange={(v) => setMaxConcurrent(typeof v === 'number' ? v : 10)}
          min={1}
          max={50}
        />
        <Alert color="blue" variant="light" fz="sm">
          Requires <code>generateBatchPdfs</code> mutation — add to mutations.graphql
          and run codegen after gateway restart.
        </Alert>
        <Button disabled fullWidth>
          Generate Batch (pending codegen)
        </Button>
      </Stack>
    </Modal>
  );
}

function PdfAComplianceModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [documentId, setDocumentId] = useState('');
  const [conformance, setConformance] = useState<string | null>('B');

  return (
    <Modal opened={opened} onClose={onClose} title="PDF/A Compliance" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Validate and convert documents to PDF/A archival format for long-term preservation.
        </Text>
        <TextInput
          label="Document ID"
          placeholder="Enter document ID..."
          value={documentId}
          onChange={(e) => setDocumentId(e.currentTarget.value)}
          required
        />
        <Select
          label="Conformance Level"
          data={[
            { value: 'B', label: 'PDF/A-3b (visual appearance)' },
            { value: 'A', label: 'PDF/A-3a (full accessibility)' },
            { value: 'U', label: 'PDF/A-3u (Unicode text)' },
          ]}
          value={conformance}
          onChange={setConformance}
        />
        <Alert color="blue" variant="light" fz="sm">
          Backend <code>pdfa.rs</code> validates conformance. Mutation endpoint
          pending — available via workflow action today.
        </Alert>
        <Button disabled fullWidth>
          Validate (pending mutation)
        </Button>
      </Stack>
    </Modal>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function PdfStudioPage() {
  const tenantId = useTenantId();
  const navigate = useNavigate();
  const isPro = FEATURE_TIER === 'pro';

  // Quick open state
  const [urlInput, setUrlInput] = useState('');
  const [activePdfUrl, setActivePdfUrl] = useState<string | null>(null);
  const [activePdfName, setActivePdfName] = useState<string>('');
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);

  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareLeftUrl, setCompareLeftUrl] = useState<string | null>(null);
  const [compareRightUrl, setCompareRightUrl] = useState<string | null>(null);
  const [compareLeftName, setCompareLeftName] = useState('');
  const [compareRightName, setCompareRightName] = useState('');

  // Modal state
  const [activeModal, setActiveModal] = useState<string | null>(null);

  // Hover state for doc cards and tool cards
  const [hoveredDocId, setHoveredDocId] = useState<string | null>(null);
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  // Hidden file input ref for tool card click → file upload flow
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Recent documents query
  const { data, loading, error } = useListDocumentsQuery({
    variables: { tenantId: tenantId ?? '', limit: 10 },
    skip: !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const recentDocs = (data?.listDocuments?.nodes ?? []).filter(
    (d) => d.mimeType === 'application/pdf',
  );

  // Stat card counts
  const stats = useMemo(() => {
    const pendingCount = recentDocs.filter(
      (d) => d.currentStatus === DocumentStatus.PENDING_APPROVAL || d.currentStatus === DocumentStatus.PENDING_VALIDATION,
    ).length;
    const regulatoryCount = recentDocs.filter(
      (d) => (d.regulatoryRuleIds?.length ?? 0) > 0,
    ).length;
    const uniqueRules = new Set<string>();
    recentDocs.forEach((d) => {
      (d.regulatoryRuleIds ?? []).forEach((r: string) => uniqueRules.add(r));
    });
    return {
      total: recentDocs.length,
      pending: pendingCount,
      regulatory: regulatoryCount,
      uniqueRules: uniqueRules.size,
    };
  }, [recentDocs]);

  const handleOpenUrl = useCallback(() => {
    if (!urlInput.trim()) return;
    setActivePdfUrl(urlInput.trim());
    setActivePdfName(urlInput.split('/').pop() ?? 'document.pdf');
    setAnnotations([]);
    setCompareMode(false);
  }, [urlInput]);

  const handleFileUpload = useCallback((file: File | null) => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setActivePdfUrl(objectUrl);
    setActivePdfName(file.name);
    setAnnotations([]);
    setCompareMode(false);
  }, []);

  const handleToolCardClick = useCallback((tool: ToolCard) => {
    if (tool.proOnly && !isPro) return;

    switch (tool.action.type) {
      case 'upload':
        fileInputRef.current?.click();
        break;
      case 'navigate':
        navigate(tool.action.to);
        break;
      case 'compare':
        setCompareMode(true);
        setActivePdfUrl(null);
        setCompareLeftUrl(null);
        setCompareRightUrl(null);
        setCompareLeftName('');
        setCompareRightName('');
        break;
      case 'modal':
        setActiveModal(tool.action.modal);
        break;
    }
  }, [isPro, navigate]);

  const handleClosePdf = useCallback(() => {
    if (activePdfUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(activePdfUrl);
    }
    if (compareLeftUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(compareLeftUrl);
    }
    if (compareRightUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(compareRightUrl);
    }
    setActivePdfUrl(null);
    setActivePdfName('');
    setAnnotations([]);
    setCompareMode(false);
    setCompareLeftUrl(null);
    setCompareRightUrl(null);
  }, [activePdfUrl, compareLeftUrl, compareRightUrl]);

  const handleAnnotationsChange = useCallback((newAnnotations: PdfAnnotation[]) => {
    setAnnotations(newAnnotations);
  }, []);

  const handleCompareFileUpload = useCallback((file: File | null, side: 'left' | 'right') => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    if (side === 'left') {
      if (compareLeftUrl?.startsWith('blob:')) URL.revokeObjectURL(compareLeftUrl);
      setCompareLeftUrl(objectUrl);
      setCompareLeftName(file.name);
    } else {
      if (compareRightUrl?.startsWith('blob:')) URL.revokeObjectURL(compareRightUrl);
      setCompareRightUrl(objectUrl);
      setCompareRightName(file.name);
    }
  }, [compareLeftUrl, compareRightUrl]);

  const isViewerActive = activePdfUrl || compareMode;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Group gap="sm" mb={4}>
              <ThemeIcon size="lg" variant="light" color="red">
                <IconFileTypePdf size={24} />
              </ThemeIcon>
              <Title order={2}>PDF Studio</Title>
              <Badge variant="light" color={isPro ? 'violet' : 'gray'} size="lg">
                {isPro ? 'Pro' : 'Free'}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed" ml={44}>
              Annotate, sign, compare, redact & export — all in one place
            </Text>
          </div>
          <FileButton onChange={handleFileUpload} accept="application/pdf">
            {(props) => (
              <Button leftSection={<IconUpload size={16} />} variant="light" {...props}>
                Upload PDF
              </Button>
            )}
          </FileButton>
        </Group>

        {/* Stat Cards */}
        {!isViewerActive && !loading && recentDocs.length > 0 && (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <StatCard icon={<IconFiles size={20} />} color="blue" label="Total PDFs" value={stats.total} />
            <StatCard icon={<IconClockHour4 size={20} />} color="orange" label="Pending Approval" value={stats.pending} />
            <StatCard icon={<IconShieldCheck size={20} />} color="teal" label="Regulatory Linked" value={stats.regulatory} />
            <StatCard icon={<IconScale size={20} />} color="cyan" label="Unique Rules" value={stats.uniqueRules} />
          </SimpleGrid>
        )}

        {/* Quick Open */}
        <Paper withBorder p="md">
          <Text fw={600} mb="xs">Quick Open</Text>
          <Group>
            <TextInput
              flex={1}
              placeholder="Enter PDF URL..."
              leftSection={<IconLink size={16} />}
              value={urlInput}
              onChange={(e) => setUrlInput(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleOpenUrl()}
            />
            <Button onClick={handleOpenUrl} disabled={!urlInput.trim()}>
              Open
            </Button>
            <Text size="sm" c="dimmed">or</Text>
            <FileButton onChange={handleFileUpload} accept="application/pdf">
              {(props) => (
                <Button variant="light" leftSection={<IconUpload size={16} />} {...props}>
                  Upload File
                </Button>
              )}
            </FileButton>
          </Group>
        </Paper>

        {/* Active PDF Viewer (single document) */}
        {activePdfUrl && !compareMode && (
          <Paper withBorder p={0}>
            <Group justify="space-between" p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Group>
                <IconFileTypePdf size={16} />
                <Text size="sm" fw={500}>{activePdfName}</Text>
              </Group>
              <Tooltip label="Close PDF">
                <ActionIcon variant="subtle" size="sm" onClick={handleClosePdf}>
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <PDFViewer
              url={activePdfUrl}
              documentId="pdf-studio-temp"
              annotations={annotations}
              onAnnotationsChange={handleAnnotationsChange}
              canAnnotate
              canSign={isPro}
              variant="full"
              featureTier={FEATURE_TIER}
              fileName={activePdfName}
              onXfaFlattened={(flatUrl) => {
                if (activePdfUrl?.startsWith('blob:')) {
                  URL.revokeObjectURL(activePdfUrl);
                }
                setActivePdfUrl(flatUrl);
                setActivePdfName(`${activePdfName} (flattened)`);
                setAnnotations([]);
              }}
            />
          </Paper>
        )}

        {/* Compare Mode (dual document) */}
        {compareMode && (
          <Paper withBorder p={0}>
            <Group justify="space-between" p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Group>
                <IconColumns size={16} />
                <Text size="sm" fw={500}>Document Comparison</Text>
                {compareLeftName && <Badge size="xs" variant="light">{compareLeftName}</Badge>}
                {compareRightName && <Badge size="xs" variant="light">{compareRightName}</Badge>}
              </Group>
              <Tooltip label="Close comparison">
                <ActionIcon variant="subtle" size="sm" onClick={handleClosePdf}>
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>

            {/* File upload for each side */}
            {(!compareLeftUrl || !compareRightUrl) && (
              <Group p="md" grow>
                <Paper withBorder p="md" ta="center" bg={compareLeftUrl ? 'var(--mantine-color-green-0)' : undefined}>
                  <Text size="sm" fw={500} mb="xs">
                    {compareLeftUrl ? compareLeftName : 'Left Document'}
                  </Text>
                  <FileButton
                    onChange={(f) => handleCompareFileUpload(f, 'left')}
                    accept="application/pdf"
                  >
                    {(props) => (
                      <Button
                        size="sm"
                        variant={compareLeftUrl ? 'light' : 'filled'}
                        leftSection={<IconUpload size={14} />}
                        {...props}
                      >
                        {compareLeftUrl ? 'Replace' : 'Upload Left PDF'}
                      </Button>
                    )}
                  </FileButton>
                </Paper>
                <Paper withBorder p="md" ta="center" bg={compareRightUrl ? 'var(--mantine-color-green-0)' : undefined}>
                  <Text size="sm" fw={500} mb="xs">
                    {compareRightUrl ? compareRightName : 'Right Document'}
                  </Text>
                  <FileButton
                    onChange={(f) => handleCompareFileUpload(f, 'right')}
                    accept="application/pdf"
                  >
                    {(props) => (
                      <Button
                        size="sm"
                        variant={compareRightUrl ? 'light' : 'filled'}
                        leftSection={<IconUpload size={14} />}
                        {...props}
                      >
                        {compareRightUrl ? 'Replace' : 'Upload Right PDF'}
                      </Button>
                    )}
                  </FileButton>
                </Paper>
              </Group>
            )}

            {/* Side-by-side comparison */}
            {compareLeftUrl && compareRightUrl && (
              <ComparisonView
                leftUrl={compareLeftUrl}
                rightUrl={compareRightUrl}
              />
            )}
          </Paper>
        )}

        {/* Recent Documents */}
        {!isViewerActive && (
          <>
            <Divider />
            <div>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Recent PDF Documents</Text>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/ferp-core/documents')}
                >
                  View All
                </Button>
              </Group>

              {loading && (
                <Center py="xl">
                  <Loader size="sm" />
                  <Text ml="sm" size="sm" c="dimmed">Loading documents...</Text>
                </Center>
              )}

              {error && (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                  Failed to load documents: {error.message}
                </Alert>
              )}

              {!loading && !error && recentDocs.length === 0 && (
                <Paper withBorder p="xl" ta="center">
                  <IconFileTypePdf size={40} style={{ opacity: 0.3 }} />
                  <Text size="sm" c="dimmed" mt="sm">
                    No PDF documents yet. Upload a PDF or open one via URL above.
                  </Text>
                </Paper>
              )}

              {recentDocs.length > 0 && (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
                  {recentDocs.map((doc) => {
                    const isHovered = hoveredDocId === doc.documentId;
                    const ruleCount = doc.regulatoryRuleIds?.length ?? 0;
                    return (
                      <Card
                        key={doc.documentId}
                        withBorder
                        padding="sm"
                        style={{
                          ...CARD_HOVER_STYLE,
                          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                          boxShadow: isHovered ? 'var(--mantine-shadow-md)' : undefined,
                        }}
                        onMouseEnter={() => setHoveredDocId(doc.documentId)}
                        onMouseLeave={() => setHoveredDocId(null)}
                        onClick={() => navigate(`/ferp-core/documents/${doc.documentId}`)}
                      >
                        <Group gap="xs" mb="xs">
                          <IconFileTypePdf size={16} color="var(--mantine-color-red-6)" />
                          <Text size="sm" fw={500} lineClamp={1} flex={1}>
                            {doc.documentName}
                          </Text>
                        </Group>
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap={4}>
                            <Badge size="xs" color={statusColor(String(doc.currentStatus))}>
                              {doc.currentStatus}
                            </Badge>
                            {ruleCount > 0 && (
                              <Tooltip label={`${ruleCount} regulatory rule${ruleCount > 1 ? 's' : ''}`}>
                                <Badge size="xs" variant="light" color="teal" leftSection={<IconShieldCheck size={10} />}>
                                  {ruleCount}
                                </Badge>
                              </Tooltip>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {relativeDate(doc.updatedAt)}
                          </Text>
                        </Group>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              )}
            </div>

            {/* Tool Cards */}
            <Divider />
            <div>
              <Text fw={600} mb="sm">PDF Tools</Text>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  handleFileUpload(file);
                  e.target.value = '';
                }}
              />
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                {TOOL_CARDS.map((tool) => {
                  const isLocked = tool.proOnly && !isPro;
                  const isHovered = hoveredTool === tool.title;
                  return (
                    <Card
                      key={tool.title}
                      withBorder
                      padding="md"
                      style={{
                        ...CARD_HOVER_STYLE,
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        transform: isHovered && !isLocked ? 'translateY(-2px)' : 'translateY(0)',
                        boxShadow: isHovered && !isLocked ? 'var(--mantine-shadow-md)' : undefined,
                        opacity: isLocked ? 0.6 : 1,
                      }}
                      onMouseEnter={() => setHoveredTool(tool.title)}
                      onMouseLeave={() => setHoveredTool(null)}
                      onClick={() => handleToolCardClick(tool)}
                    >
                      <Group gap="sm" mb="xs">
                        <ThemeIcon
                          variant="light"
                          size="lg"
                          color={isLocked ? 'gray' : 'blue'}
                        >
                          {tool.icon}
                        </ThemeIcon>
                        <div style={{ flex: 1 }}>
                          <Group gap={4}>
                            <Text fw={600} size="sm">{tool.title}</Text>
                            {isLocked && (
                              <Badge size="xs" variant="light" color="gray" leftSection={<IconLock size={10} />}>
                                Pro
                              </Badge>
                            )}
                          </Group>
                        </div>
                      </Group>
                      <Text size="xs" c="dimmed">{tool.description}</Text>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </div>
          </>
        )}
      </Stack>

      {/* Modals */}
      <EncryptModal
        opened={activeModal === 'encrypt'}
        onClose={() => setActiveModal(null)}
        tenantId={tenantId}
      />
      <BatchGenerateModal
        opened={activeModal === 'batch'}
        onClose={() => setActiveModal(null)}
      />
      <PdfAComplianceModal
        opened={activeModal === 'pdfa'}
        onClose={() => setActiveModal(null)}
      />
      <FormFillModal
        opened={activeModal === 'formfill'}
        onClose={() => setActiveModal(null)}
      />
    </Container>
  );
}
